import { dao } from '@platform/dao';
import type { IMyWorkWire } from '@platform/dao';
import type { IRecordBatch, IRecordListParams } from '@platform/core/types';
import type { IWorkspaceRecordsArg } from './args/IWorkspaceRecordsArg';
import { baseApi, listTag, run } from './baseApi';
import { myWorkTag } from './myWorkTag';

/** Стеля записів одного типу для зведень робочого простору (бізнес-аналітика, огляд напряму): сторінки по 100. */
const TYPE_CAP = 500;

/**
 * Записи кількох типів робочого простору одним запитом (кожен тип — посторінково до стелі; тип, який не вдалося прочитати, пропускається).
 * `total` — сума total типів. Кешується зі списковими тегами цих типів: зміна запису оновлює зведення.
 */
async function collect(ws: string, types: string[], params: IRecordListParams, cap: number): Promise<IRecordBatch> {
  const pages = await Promise.allSettled(types.map((rt) => dao.records.listAll(ws, rt, params, cap)));
  const ok = pages.flatMap((p) => (p.status === 'fulfilled' ? [p.value] : []));
  return { items: ok.flatMap((p) => p.items), total: ok.reduce((sum, p) => sum + p.total, 0) };
}

/**
 * «Моя робота» (прототип v4, Головна «Призначені вам», лічильники меню «Моя робота», подання /work): GET /me/work?workspaceId= — призначені
 * мені записи всіх модулів простору зі статусом поза DONE, за терміном, і лічильники; оновлюється після кожної зміни запису (myWorkTag).
 * Записи робочого простору (бізнес-аналітика, огляд напряму): усі записи головних таблиць вибраних модулів.
 */
export const myWorkApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    myWork: b.query<IMyWorkWire, string>({
      queryFn: (ws) => run(() => dao.me.work(ws)),
      providesTags: [myWorkTag],
    }),
    workspaceRecords: b.query<IRecordBatch, IWorkspaceRecordsArg>({
      queryFn: ({ ws, types }) => run(() => collect(ws, types, {}, TYPE_CAP)),
      providesTags: (_r, _e, { types }) => types.map(listTag),
    }),
  }),
});

export const { useMyWorkQuery, useWorkspaceRecordsQuery } = myWorkApi;
